import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Modal } from 'semantic-ui-react';
import { findWithUUID } from '@/utils/uuidHelper';
import newError, { clientErrors, firstResponseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import { applyDictionaries, hideDictionaryField, updateLookupProperties } from '../../dictionaries/actions';
import validateLookupItems, { transformLookups } from '../../lookups/helper';
import FieldInformation from '../components/FieldInformation';
import FieldInformationHeader from '../components/FieldInformationHeader';
import DictionaryFieldActions from './DictionaryFieldActions';
import { canEditDataset } from '../../helper';
import { resolveCatalogueTemplate } from '../../catalogue/helper';

const DictionaryFieldDialog = ({
  applyChanges,
  canEdit,
  category,
  code,
  dictionaries,
  fields,
  fieldUUID,
  hide,
  lookups,
  saveErrors,
  template,
  theme,
  updateProperties,
  validationWarnings,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => updateProperties({ saveErrors: newErrors });

  const setStatus = (status) => updateProperties({ saveStatus: status });

  const validateForm = () => {
    const validationErrors = [];

    dictionaries.forEach((dictionary) => {
      const validationDictionary = {
        ...dictionary,
        lookups: transformLookups(dictionary.lookups),
      };
      validateLookupItems(validationDictionary, validationErrors, intl);
    });

    if (validationErrors.length > 0) {
      validationErrors.unshift(
        newError(
          intl.formatMessage({ id: 'validation.general.invalid.title' }),
          null,
          intl.formatMessage({ id: 'validation.general.invalid' }),
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const handleDismiss = () => {
    setErrors(clientErrors(saveErrors));
  };

  const submitForm = () => {
    setStatus(states.PENDING);
    const validationErrors = checkForErrors();

    if (validationErrors.length > 0) {
      setStatus(states.NONE);
    } else {
      applyChanges(template);
    }
  };

  return (
    <DialogWrapper
      className="aridhia-dictionary-field-dialog smallwizard"
      error={firstResponseError(saveErrors)}
      onClose={hide}
      onDismiss={handleDismiss}
      size="large"
      theme={theme}
      title={intl.formatMessage({ id: 'dictionary.fields.title' })}
    >
      <Modal.Content scrolling>
        <FieldInformationHeader category={category} code={code} errors={saveErrors} />
        <FieldInformation
          field={findWithUUID(fields, fieldUUID)}
          lookups={lookups}
          canEdit={canEdit}
          errors={saveErrors}
          validationWarnings={validationWarnings}
        />
      </Modal.Content>
      <DictionaryFieldActions onClose={hide} onSave={submitForm} />
    </DialogWrapper>
  );
};

const mapStateToProps = ({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets, lookups },
}) => {
  const dictionary = findWithUUID(lookups.dictionaries, lookups.activeDictionaryUUID);
  const { template } = resolveCatalogueTemplate(datasets, templates, lookups);

  return (
    {
      canEdit: canEditDataset(lookups, role),
      category: lookups.category,
      code: lookups.code,
      dictionaries: lookups.dictionaries,
      fieldUUID: lookups.activeFieldUUID,
      fields: dictionary ? dictionary.fields || [] : [],
      lookups: dictionary ? dictionary.lookups || {} : {},
      saveErrors: lookups.saveErrors,
      validationWarnings: lookups.validationWarnings,
      template,
    }
  );
};

const mapDispatchToProps = {
  applyChanges: applyDictionaries,
  hide: hideDictionaryField,
  updateProperties: updateLookupProperties,
};

export default connect(mapStateToProps, mapDispatchToProps)(DictionaryFieldDialog);
